import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaWhatsapp, FaChevronDown, FaPaperPlane, FaRedo } from 'react-icons/fa';
import { useChatbotDeuda } from '../../../../hooks/useChatbotDeuda';

export const ChatOption = ({ label, onClick, icon: Icon, green = false }) => (
  <button
    onClick={onClick}
    className={`w-full p-3 rounded-xl border text-left text-[11px] font-bold transition-all flex items-center justify-between ${green ? "bg-green-50 border-green-100 text-green-700 hover:bg-green-100" : "bg-white border-gray-100 text-blue-700 hover:border-blue-200 hover:bg-blue-50"}`}
  >
    {label}
    {Icon ? (
      <Icon size={12} className={green ? "text-green-500" : "text-blue-400"} />
    ) : (
      <FaChevronDown size={8} className="-rotate-90 opacity-30" />
    )}
  </button>
);

const MessageBubble = ({ text, sender = 'bot', options = null }) => {
  const isBot = sender === 'bot';
  return (
    <div className={`w-full animate-fade-in-up mb-2 ${isBot ? '' : 'flex justify-end'}`}>
      <div className={`${isBot ? 'w-full px-4 py-2.5' : 'max-w-[90%] px-3.5 py-1.5'} rounded-xl text-sm leading-snug shadow-sm ${
        isBot ? 'bg-white text-gray-700 border border-gray-100 rounded-tl-none' : 'bg-blue-600 text-white rounded-tr-none'
      }`}>
        <p style={{ whiteSpace: 'pre-line' }}>{text}</p>
      </div>
      {options && <div className="space-y-2 pt-2">{options}</div>}
    </div>
  );
};

export const ChatbotModal = ({ chatOpen, setChatOpen, changeView, openWhatsApp }) => {
  const [step, setStep] = useState('menu');
  const [chatHistory, setChatHistory] = useState([]);
  const [medidorInput, setMedidorInput] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [unreadAlert, setUnreadAlert] = useState(true);
  
  const messagesEndRef = useRef(null);
  const { consultarDeuda, loading } = useChatbotDeuda();

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => { scrollToBottom(); }, [chatHistory, step, loading]);

  // Initial welcome message
  useEffect(() => {
    if (chatHistory.length === 0) {
      setChatHistory([{
        id: 1, sender: 'bot', text: 'Hola 👋 Soy Poli Boot tu asistente virtual. ¿Cómo puedo ayudarte hoy?',
        isMenu: true
      }]);
    }
  }, [chatHistory]);

  // Clear unread badge when opened
  useEffect(() => {
    if (chatOpen) setUnreadAlert(false);
  }, [chatOpen]);

  const addMessage = (sender, text, isMenu = false) => {
    setChatHistory(prev => [...prev, { id: Date.now(), sender, text, isMenu }]);
  };

  const handleConsultarDeudaClick = () => {
    addMessage('user', 'Consultar Deuda');
    setTimeout(() => {
      addMessage('bot', 'Por favor, ingresa tu código de medidor para consultar tu deuda.');
      setStep('ask_medidor');
    }, 500);
  };

  const resetChat = () => {
    setChatHistory([{
      id: Date.now(), sender: 'bot', text: 'Hola 👋 Soy Poli Boot tu asistente virtual. ¿Cómo puedo ayudarte hoy?',
      isMenu: true
    }]);
    setStep('menu');
    setMedidorInput('');
    setInputValue('');
  };

  const handleSendInput = async (e) => {
    e.preventDefault();
    const val = inputValue.trim();
    if (!val) return;
    
    setInputValue('');
    addMessage('user', val);

    if (step === 'ask_medidor') {
      setMedidorInput(val);
      setTimeout(() => {
        addMessage('bot', 'Perfecto. Ahora, ingresa tu número de cédula para validar tu identidad.');
        setStep('ask_cedula');
      }, 500);
    } else if (step === 'ask_cedula') {
      setStep('fetching');
      
      // Llamada a la API
      const result = await consultarDeuda(medidorInput, val);
      
      if (result) {
        if (result.deudas && result.deudas.length > 0) {
          const total = result.deudas.reduce((sum, d) => sum + parseFloat(d.saldo_pendiente), 0).toFixed(2);
          addMessage('bot', `Hola ${result.cliente},\n\nTienes ${result.deudas.length} facturas pendientes.\nTotal a pagar: $${total}\n\nPuedes ir al portal principal para más detalles.`);
        } else {
          addMessage('bot', `Hola ${result.cliente},\n\nActualmente estás al día. No registras deudas pendientes.`);
        }
      } else {
        addMessage('bot', 'No encontramos información con esos datos. Verifica tu medidor o cédula e intenta nuevamente.');
      }
      setStep('result');
    }
  };

  return (
    <>
      {/* --- MINIMALIST CHATBOT TRIGGER --- */}
      <div className={`fixed bottom-8 right-8 z-[60] transition-all duration-500 ${chatOpen ? "translate-y-4 opacity-0 pointer-events-none" : "translate-y-0 opacity-100"}`}>
        <button onClick={() => setChatOpen(true)} className="relative group w-28 h-28 flex items-center justify-center transition-all hover:scale-110 active:scale-95">
          <div className="absolute inset-0 bg-blue-600/10 rounded-full blur-3xl group-hover:bg-blue-600/20 transition-all animate-pulse"></div>
          <div className="relative w-24 h-24 bg-white rounded-full p-2 shadow-2xl border border-gray-100 overflow-hidden flex items-center justify-center">
            <img src="/chatboot.gif" alt="Poli Boot" className="w-full h-full object-cover scale-150" />
          </div>
          
          {unreadAlert && (
            <div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white animate-bounce shadow-md z-10">
              1
            </div>
          )}

          <div className="absolute right-full mr-4 bg-white px-6 py-4 rounded-3xl shadow-xl border border-gray-100 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0 pointer-events-none">
            <p className="text-base font-black text-blue-900 whitespace-nowrap">
              {unreadAlert ? '¡Tengo un mensaje para ti!' : '¿Necesitas ayuda?'}
            </p>
          </div>
          <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-500 border-4 border-white rounded-full shadow-sm"></div>
        </button>
      </div>

      {/* --- CHATBOT INTERFACE --- */}
      <div className={`fixed bottom-8 right-8 z-[60] w-80 h-[500px] max-h-[85vh] bg-white rounded-[24px] shadow-2xl border border-gray-100 overflow-hidden transition-all duration-500 flex flex-col transform ${chatOpen ? "translate-y-0 opacity-100 scale-100" : "translate-y-12 opacity-0 scale-90 pointer-events-none"}`}>
        {/* Header */}
        <div className="bg-blue-900 p-3.5 text-white relative shrink-0">
          <button onClick={() => setChatOpen(false)} className="absolute top-3 right-3 opacity-50 hover:opacity-100 transition-opacity p-2">
            <FaTimes size={14} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center relative overflow-hidden shadow-sm border border-white/20 shrink-0">
              <img src="/chatboot.gif" alt="Poli Boot" className="w-full h-full object-cover scale-125" />
              <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-green-500 border-2 border-blue-900 rounded-full"></div>
            </div>
            <div>
              <h4 className="font-bold text-sm leading-tight">Asistente Virtual</h4>
              <p className="text-xs text-blue-300 font-medium tracking-tight mt-0.5">Soporte Técnico</p>
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="flex-grow overflow-y-auto p-4 bg-gray-50/50 flex flex-col scroll-smooth">
          {chatHistory.map((msg) => (
            <MessageBubble 
              key={msg.id} 
              text={msg.text} 
              sender={msg.sender} 
              options={
                msg.isMenu && step === 'menu' ? (
                  <>
                    <ChatOption label="Consultar Deuda" onClick={handleConsultarDeudaClick} />
                    <ChatOption label="Reportar Pago" onClick={() => openWhatsApp("Hola, deseo reportar un pago.")} />
                    <ChatOption label="Soporte Técnico" onClick={() => openWhatsApp("Hola, necesito soporte técnico con la plataforma.")} icon={FaWhatsapp} green />
                  </>
                ) : null
              }
            />
          ))}

          {loading && (
            <div className="flex justify-start animate-pulse mb-3">
              <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none p-3 shadow-sm flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input Area */}
        {step !== 'menu' && (
          <div className="p-4 border-t border-gray-100 bg-white shrink-0">
            {step === 'result' ? (
              <button
                onClick={resetChat}
                className="w-full py-2.5 bg-blue-50 text-blue-600 font-bold text-xs rounded-xl hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
              >
                <FaRedo /> Hacer otra consulta
              </button>
            ) : (
              <form onSubmit={handleSendInput} className="flex gap-2">
                <input
                  type={step === 'ask_cedula' ? 'number' : 'text'}
                  placeholder={step === 'ask_medidor' ? 'Escribe el código...' : 'Escribe tu cédula...'}
                  className="flex-grow bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || loading}
                  className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 shadow-md"
                >
                  <FaPaperPlane size={12} className="-ml-0.5" />
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
};

